import { colors } from '@/utils/theme';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { BLOCKS, MARKS, INLINES, Document } from '@contentful/rich-text-types';
import {
  Box,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Heading,
  Image,
} from '@everlywell/ui-kit';
import React from 'react';

// Types
interface IconSectionItem {
  title: string;
  text: string[];
  icon: string;
}

interface AccordionSection {
  title: string;
  content: string;
}

export interface ProductAccordion {
  title: string;
  description?: Document[]; // Using proper Contentful Rich Text Document type
  features?: string[];
  sections?: AccordionSection[];
  iconSection?: IconSectionItem[];
  symptoms?: IconSectionItem[];
}

interface Props {
  accordions?: ProductAccordion[];
}

// Utility function moved outside component to prevent recreation
const createFeaturesHtml = (features: string[]): string => {
  if (!features?.length) return '';

  return `
    <ul style="list-style-type: disc !important; padding-left: 20px; margin-top: 16px; margin-bottom: 16px;">
      ${features
        .map(
          (feature) => `
        <li style="margin-bottom: 12px; color: #545454; font-size: 16px; line-height: 24px; display: list-item !important;" data-testid="feature-item">
          ${feature.replace(/<\/?p>/g, '')}
        </li>
      `,
        )
        .join('')}
    </ul>
  `;
};

// Rich text render options moved outside component to prevent recreation
const richTextRenderOptions = {
  renderMark: {
    [MARKS.BOLD]: (text: string) =>
      `<strong style="font-weight: 600;">${text}</strong>`,
    [MARKS.ITALIC]: (text: string) => `<em>${text}</em>`,
    [MARKS.UNDERLINE]: (text: string) => `<u>${text}</u>`,
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: any, next: any) =>
      `<p>${next(node.content)}</p>`,
    [BLOCKS.HEADING_1]: (node: any, next: any) =>
      `<h1 style="font-size: 2em; margin-bottom: 1rem;">${next(
        node.content,
      )}</h1>`,
    [BLOCKS.HEADING_2]: (node: any, next: any) =>
      `<h2 style="font-size: 1.5em; margin-bottom: 1rem;">${next(
        node.content,
      )}</h2>`,
    [BLOCKS.HEADING_3]: (node: any, next: any) =>
      `<h3 style="font-size: 1.17em; margin-bottom: 1rem;">${next(
        node.content,
      )}</h3>`,
    [BLOCKS.UL_LIST]: (node: any, next: any) =>
      `<ul style="list-style-type: disc; padding-left: 1.5rem; margin-bottom: 1rem;">${next(
        node.content,
      )}</ul>`,
    [BLOCKS.OL_LIST]: (node: any, next: any) =>
      `<ol style="list-style-type: decimal; padding-left: 1.5rem; margin-bottom: 1rem;">${next(
        node.content,
      )}</ol>`,
    [BLOCKS.LIST_ITEM]: (node: any, next: any) =>
      `<li style="margin-bottom: 0.5rem;">${next(node.content)}</li>`,
    [INLINES.HYPERLINK]: (node: any, next: any) =>
      `<a href="${node.data.uri}" style="color: ${
        colors.primary.main
      }; text-decoration: underline;">${next(node.content)}</a>`,
  },
};

const descriptionStyles = {
  h3: {
    fontSize: 'md',
    fontWeight: '600',
    color: '#0F0F0F',
    mt: '2rem',
    mb: '1rem',
    display: 'block',
  },
  p: {
    color: '#545454',
    fontSize: '16px',
    mb: '1rem',
    lineHeight: '1.6',
    display: 'block',
  },
  strong: {
    fontWeight: '600',
    color: '#0F0F0F',
    display: 'inline',
  },
  '> div': {
    display: 'block',
  },
} as const;

export default function ProductAccordions({
  accordions,
}: Props): JSX.Element | null {
  if (!accordions?.length) return null;

  return (
    <Box
      backgroundColor="#F9F9F6"
      p={8}
      borderRadius="lg"
      className="accordion-card"
    >
      <Accordion allowMultiple data-testid="product-accordion">
        {accordions.map((accordion, index) => (
          <AccordionItem
            key={`accordion-${index}-${accordion.title}`}
            border="none"
            borderBottom={
              index < accordions.length - 1 ? '1px solid #EEEEEE' : 'none'
            }
          >
            <h2>
              <AccordionButton
                pt={index === 0 ? 0 : undefined}
                aria-label={`Toggle ${accordion.title} section`}
                _focus={{ boxShadow: 'outline' }}
              >
                <Box flex="1" textAlign="left">
                  <Text fontSize="lg" color="#0F0F0F" fontWeight="500">
                    {accordion.title}
                  </Text>
                </Box>
                <AccordionIcon color="#28724F" />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              {accordion.description?.map((desc, i) => {
                let html = '';
                try {
                  html = documentToHtmlString(desc, richTextRenderOptions);
                } catch {
                  // If parsing fails, return empty string
                  html = '';
                }

                return (
                  <Box
                    key={`desc-${i}`}
                    className="accordion-description"
                    data-testid="accordion-description"
                    sx={{
                      ...descriptionStyles,
                      '& a': {
                        color: colors.primary.main,
                        textDecoration: 'underline',
                      },
                      '& p': {
                        marginBottom: '1rem',
                      },
                      '& strong': {
                        display: 'block',
                        marginTop: '1rem',
                      },
                      '& ul, & ol': {
                        paddingLeft: '1.5rem',
                        marginBottom: '1rem',
                      },
                      '& li': {
                        marginBottom: '0.5rem',
                      },
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        marginBottom: '1rem',
                        fontWeight: '500',
                      },
                    }}
                  >
                    <div
                      dangerouslySetInnerHTML={{ __html: html }}
                      aria-label={`Description section ${i + 1}`}
                    />
                  </Box>
                );
              })}

              {accordion.sections?.map((section, i) => (
                <Text
                  key={`section-${i}-${section.title}`}
                  color="#545454"
                  fontSize="md"
                  mt={'2rem'}
                >
                  <Heading as="h3" fontSize="md" color="#0F0F0F" mb={2}>
                    {section.title}
                  </Heading>
                  <Text color="#545454" fontSize="md">
                    {section.content}
                  </Text>
                </Text>
              ))}

              {accordion.features && accordion.features.length > 0 && (
                <div
                  data-testid="accordion-features"
                  dangerouslySetInnerHTML={{
                    __html: createFeaturesHtml(accordion.features),
                  }}
                  aria-label="Features list"
                />
              )}

              {accordion.iconSection && accordion.iconSection.length > 0 && (
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={8}
                  mt={6}
                  role="list"
                  aria-label="Icon sections list"
                >
                  {accordion.iconSection.map((section, i) => (
                    <Box
                      key={`icon-section-${i}-${section.title}`}
                      flex={['1 1 30%', '1 1 25%', '1 1 25%']}
                      display="flex"
                      flexDirection="column"
                      role="listitem"
                    >
                      <Box mb={4}>
                        <Image
                          src={section.icon}
                          alt={`${section.title} icon`}
                          width="48px"
                          height="48px"
                        />
                      </Box>
                      <Heading as="h3" fontSize="md" color="#0F0F0F" mb={2}>
                        {section.title}
                      </Heading>
                      <Box
                        as="ul"
                        listStyleType="none"
                        pl={0}
                        flex="1"
                        role="list"
                      >
                        {section.text.map((item, j) => (
                          <Box
                            as="li"
                            key={`text-${j}`}
                            color="#545454"
                            fontSize="md"
                            mb={2}
                            role="listitem"
                          >
                            {item}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {accordion.symptoms && accordion.symptoms.length > 0 && (
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={8}
                  mt={6}
                  role="list"
                  aria-label="Symptoms list"
                >
                  {(accordion.symptoms || []).map((section, i) => (
                    <Box
                      key={`symptom-${i}-${section.title}`}
                      flex={['1 1 30%', '1 1 25%', '1 1 25%']}
                      display="flex"
                      flexDirection="column"
                      role="listitem"
                    >
                      <Box mb={4}>
                        <Image
                          src={section.icon}
                          alt={`${section.title} icon`}
                          width="48px"
                          height="48px"
                        />
                      </Box>
                      <Heading as="h3" fontSize="md" color="#0F0F0F" mb={2}>
                        {section.title}
                      </Heading>
                      <Box
                        as="ul"
                        listStyleType="none"
                        pl={0}
                        flex="1"
                        role="list"
                      >
                        {section.text.map((item, j) => (
                          <Box
                            as="li"
                            key={`text-${j}`}
                            color="#545454"
                            fontSize="md"
                            mb={2}
                            role="listitem"
                          >
                            {item}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    </Box>
  );
}
